<!--
	A Yes/No, Ok/Cancel or similar prompt.
-->
<template>
	<div>
		<Message :toast="toast" :portal="getPortalId()">
			<!--
				We do not use v-html here, i.e., all HTML is fully escaped. If you want to display
				tags, you can set the default slot when embedding this into other Vue components.
				However, note that not allowing HTML here is on purpose as we do not know whether
				message can be trusted or not.
				-->
			<slot>{{ message }}</slot>
		</Message>
		<Button v-for="(label, i) of labels" :key="i" :toast="toast" :portal="getPortalId(i.toString())">
			<!-- We do not allow any HTML markup here. But note that the first button is always bold. -->
			<span>{{ label }}</span>
		</Button>
	</div>
</template>
<script lang="ts">
import { Component, toNative, Watch } from "vue-facing-decorator";
import { type SnotifyToastConfig, type SnotifyType } from "vue3-snotify";
import type { IChoice, IPrompt } from "./PromptTypes";
import Button from "./Shared/Button.vue";
import Message from "./Shared/Message.vue";
import { SNotification } from "./Shared/SNotification";

@Component({
	components: {
		Button,
		Message,
	},
})
export class Prompt extends SNotification<IPrompt> {
	message = "…" as string;
	labels = [] as string[];

	@Watch("from.message", { immediate: true })
	async onMessageChanged(message: string | Promise<string>) {
		void await this.from.message;
		if (message === this.from.message) {
			this.message = await this.from.message;
		}
	}

	@Watch("from.choices", { immediate: true })
	async onChoicesChanged(choices: IChoice<any>[]) {
		const labels = await Promise.all(this.from.choices.map((choice) => choice.label));
		if (choices === this.from.choices) {
			this.labels = labels;
		}
	}

	protected getToastConfig(): SnotifyToastConfig {
		const config = this.getDefaultToastConfig();
		config.closeOnClick = false;
		config.buttons = this.from.choices.map((choice, i) => ({
			action: () => {
				this.hide();
				choice.value();
			},
			bold: i === 0,
			text: this.getPortalId(i.toString()),
		}));
		config.type = "Prompt" as SnotifyType;
		return config;
	}
}

export default toNative(Prompt);
</script>
